import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react'
import { Box, Typography, Alert, TextField, CircularProgress, useTheme } from '@mui/material'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AppContext } from 'components/AppContextProvider'
import { Sidebar } from 'components/sidedrawer/Sidebar2'
import { useApi } from 'auth/useApi3'
import { IApiFunc, IApiResult } from 'auth/interface/api.interface'
import { apiEndpoints } from 'generated/apiEndpoints'
import { useTranslation } from 'react-i18next'
import {
  StyledContainer,
  StyledServiceInfoBox,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
  StyledFormSection,
  StyledSectionTitle,
} from './ServiceConfirmationSidebar.styled'
import { ServiceConfirmationFormValues, ServiceConfirmationSidebarProps, IoTOdometerStatus, ServiceConfirmationData } from './ServiceConfirmationSidebar.interfaces'
import { ServiceConfirmationValidationSchema2 } from './ServiceConfirmationSidebar.validation'
import { Autocomplete } from '@mui/material'
import { formatDateForApi, showDate } from 'components/DateHandler/dateHandler'
import { faCircleInfo, faWrench } from '@fortawesome/pro-solid-svg-icons'
import { StyledInputLabel } from 'components/Form/FormComponents/FormSwitch/FormSwitch.styled'
import { StyledAccordionDetails, StyledAccordionSummary } from '../InspectionBookingSidebar/InspectionBookingSidebar.styled'
import { StyledAccordion } from 'shared/styled/General'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const ServiceDateTextField: React.FC = () => {
  const { values, setFieldValue, touched, errors } = useFormikContext<ServiceConfirmationFormValues>()
  const { t } = useTranslation()

  return (
    <Box>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">{t("checks.service_date", "Service date")}</Typography>
      </StyledInputLabel>

      <DatePicker
        disableFuture
        format="YYYY-MM-DD"
        value={values.serviceDate ? moment(values.serviceDate) : null}
        onChange={(newValue) => {
          setFieldValue("serviceDate", newValue?.isValid() ? newValue.toDate() : null)
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            error: Boolean(touched.serviceDate && errors.serviceDate),
            helperText: touched.serviceDate && errors.serviceDate ? String(errors.serviceDate) : undefined,
          }
        }}
      />
    </Box>

  )
}

const ServiceMeterTextField: React.FC = () => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<ServiceConfirmationFormValues>()
  const { t } = useTranslation()

  return (
    <TextField
      label={`${t("checks.service_meter_setting", "Service Meter Setting")} (km)`}
      type="number"
      fullWidth
      id="serviceMeterSetting"
      value={values.serviceMeterSetting || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(touched.serviceMeterSetting && errors.serviceMeterSetting)}
      helperText={(touched.serviceMeterSetting && errors.serviceMeterSetting) || t("checks.validation.service_meter_required", "Service meter setting is required for distance-based intervals")}
      variant="outlined"
      required
    />
  )
}

interface CurrentMeterTextFieldProps {
  iotStatus?: IoTOdometerStatus | null
}

const CurrentMeterTextField: React.FC<CurrentMeterTextFieldProps> = ({ iotStatus }) => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<ServiceConfirmationFormValues>()
  const { t } = useTranslation()

  if (iotStatus?.hasIoTDevice && iotStatus?.currentOdometer) return (
    <Alert variant="outlined" severity="success" sx={{ mt: 1 }}>
      {t("global.iot_odometer_detected", "IoT device detected") + ". " + t("global.current_odometer", "Current odometer")}:
      <strong> {Math.round(iotStatus?.currentOdometer)} km</strong>
      {iotStatus.lastUpdated && (
        <Typography variant="caption" display="block">
          {t("global.last_updated", "Last updated")}: {showDate(iotStatus.lastUpdated)}
        </Typography>
      )}
    </Alert>
  )

  return (
    <Box>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">{`${t("global.current_meter_setting", "Current meter setting")} (${t("global.optional", "Optional")})`}</Typography>
      </StyledInputLabel>

      <TextField
        type="number"
        fullWidth
        id="currentMeterSetting"
        value={values.currentMeterSetting || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.currentMeterSetting && errors.currentMeterSetting ? true : false}
        helperText={
          touched.currentMeterSetting && errors.currentMeterSetting ?
            errors.currentMeterSetting :
            t("checks.manual_odometer_update", "Manual update required - no IoT device detected")
        }
        variant="outlined"
        required={!iotStatus?.hasIoTDevice}
      />
    </Box>
  )
}

const WorkshopField: React.FC<{ workshops: any[] }> = ({ workshops }) => {
  const { values, setFieldValue, touched, errors } = useFormikContext<ServiceConfirmationFormValues>()
  const { t } = useTranslation()

  return (
    <Box>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">{t("global.workshop", "Workshop")}</Typography>
      </StyledInputLabel>

      <Autocomplete
        disablePortal
        fullWidth
        options={workshops}
        getOptionLabel={(option) => option.name || ''}
        value={workshops.find(w => w.id === values.carWorkshopId) || null}
        onChange={(_, newValue) => {
          setFieldValue('carWorkshopId', newValue?.id || null)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            error={Boolean(touched.carWorkshopId && errors.carWorkshopId)}
            helperText={touched.carWorkshopId && errors.carWorkshopId}
          />
        )}
      />
    </Box>
  )
}

const titalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

const RemarkTextField: React.FC = () => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<ServiceConfirmationFormValues>()
  const { t } = useTranslation()

  return (
    <Box>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">{t("global.notes", "Notes")}</Typography>
      </StyledInputLabel>

      <TextField
        id="remark"
        fullWidth
        multiline
        rows={4}
        value={values.remark}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched.remark && errors.remark)}
        helperText={touched.remark && errors.remark ? errors.remark : null}
        variant="outlined"
      />
    </Box>
  )
}

const ServiceInfoBox: React.FC<{ service: any }> = ({ service }) => {
  const { t } = useTranslation()

  return (
    <StyledServiceInfoBox>
      <StyledSectionTitle variant="h6">
        {t('global.vehicle_information', 'Vehicle information')}
      </StyledSectionTitle>

      <StyledInfoRow>
        <StyledInfoLabel>{t('global.reg_number_short', 'Reg No')}:</StyledInfoLabel>
        <StyledInfoValue>{service?.vehicleRegNumber || '-'}</StyledInfoValue>
      </StyledInfoRow>

      <StyledInfoRow>
        <StyledInfoLabel>{t('global.internal_number_short', 'Internal No')}:</StyledInfoLabel>
        <StyledInfoValue>{service?.vehicleInternNumber || '-'}</StyledInfoValue>
      </StyledInfoRow>

      <StyledInfoRow>
        <StyledInfoLabel>{t('global.check_template', 'Check template')}:</StyledInfoLabel>
        <StyledInfoValue>{service?.checkTemplateName || '-'}</StyledInfoValue>
      </StyledInfoRow>

      <StyledInfoRow>
        <StyledInfoLabel>{t('global.checktype', 'Check type')}:</StyledInfoLabel>
        <StyledInfoValue>
          {titalize(t(`global.${service?.intervalType?.toLowerCase()}`, 'Mixed'))}
        </StyledInfoValue>
      </StyledInfoRow>

      {service?.lastCompletedDate && (
        <StyledInfoRow>
          <StyledInfoLabel >{t('checks.last_service_date', 'Last service date')}:</StyledInfoLabel>
          <StyledInfoValue>{showDate(service.lastCompletedDate)}</StyledInfoValue>
        </StyledInfoRow>
      )}

      {service?.lastCompletedDistance !== null && service?.lastCompletedDistance !== undefined && (
        <StyledInfoRow>
          <StyledInfoLabel>{t('checks.last_service_distance', 'Last service distance')}:</StyledInfoLabel>
          <StyledInfoValue>{service.lastCompletedDistance} km</StyledInfoValue>
        </StyledInfoRow>
      )}
    </StyledServiceInfoBox>
  )
}

const formId = 'ServiceConfirmationSidebar'

export const ServiceConfirmationSidebar: React.FC<ServiceConfirmationSidebarProps> = ({
  vehicleId,
  checkTemplateId,
  onConfirmed
}) => {
  const appContext = useContext(AppContext)
  const [intervalApiResult, intervalApi] = useApi() as [IApiResult, IApiFunc]
  const [workshopsResult, workshopsApi] = useApi() as [IApiResult, IApiFunc]
  const [iotStatusResult, iotStatusApi] = useApi() as [IApiResult, IApiFunc]
  const [_, updateOdometerApi] = useApi() as [IApiResult, IApiFunc]
  const [serviceDataResult, serviceDataApi] = useApi() as [IApiResult, IApiFunc]
  const [service, setService] = useState<ServiceConfirmationData | null>(null)
  const [workshops, setWorkshops] = useState<any[]>([])
  const [iotStatus, setIotStatus] = useState<IoTOdometerStatus | null>(null)
  const [loadingIoT, setLoadingIoT] = useState(false)
  const formRef = useRef<any>()
  const { t } = useTranslation()
  const theme = useTheme()

  const initialFormValues: ServiceConfirmationFormValues = useMemo(() => ({
    serviceDate: new Date(),
    serviceMeterSetting: null, // Optional service meter reading
    currentMeterSetting: service?.currentOdometerReading || null, // Current meter reading for manual update
    notes: '',
    carWorkshopId: null,
    remark: '',
  }), [service?.currentOdometerReading])

  const [initialFormikValues, setInitialFormikValues] = useState(initialFormValues)

  const handleSubmit = async (values: ServiceConfirmationFormValues) => {
    if (!service) return

    if (!iotStatus?.hasIoTDevice && values?.currentMeterSetting && values?.currentMeterSetting > (service.currentOdometerReading || 0)) {
      const url = apiEndpoints.vehicleregistry.updatevehicleodometer
      updateOdometerApi.put(url, { vehicleId: service?.vehicleId, odometerReading: values.currentMeterSetting })
    }

    const confirmServiceData = {
      vehicleId: service.vehicleId,
      checkTemplateId: service.checkTemplateId,
      confirmedDate: formatDateForApi(values.serviceDate),
      confirmedDistance: values.serviceMeterSetting, // Use optional service meter setting
      updateAllIntervals: true,
      carWorkshopId: values.carWorkshopId,
      remark: values.remark,
      notes: values.notes,
    }

    let url = apiEndpoints.checktemplate.confirmservice
    intervalApi.post(url, confirmServiceData)
  }

  useEffect(() => {
    if (intervalApiResult.status !== 2) return

    if (onConfirmed) {
      onConfirmed()
    }
    appContext.toggleSideBar(formId, false)
  }, [intervalApiResult, onConfirmed, appContext])

  useEffect(() => {
    if (appContext.isSidebarOpen !== formId) return
    formRef?.current?.resetForm()
    setInitialFormikValues(initialFormValues)
    setIotStatus(null)
  }, [appContext.isSidebarOpen, initialFormValues])

  useEffect(() => {
    if (!service) return
    setInitialFormikValues(initialFormValues)
  }, [service, initialFormValues])

  // Fetch service data when sidebar opens
  useEffect(() => {
    if (appContext.isSidebarOpen === formId && vehicleId && checkTemplateId) {
      const url = apiEndpoints.checktemplate.getserviceforconfirmation.replace("{checkTemplateId}", checkTemplateId?.toString()).replace("{vehicleId}", vehicleId?.toString())
      serviceDataApi.get(url)
    }
  }, [appContext.isSidebarOpen, vehicleId, checkTemplateId, serviceDataApi])

  // Handle service data fetch result
  useEffect(() => {
    if (serviceDataResult.status === 2 && serviceDataResult.data) {
      setService(serviceDataResult.data)
    } else if (serviceDataResult.status === 3) {
      console.error('Failed to fetch service data', serviceDataResult)
    }
  }, [serviceDataResult])

  useEffect(() => {
    if (!service?.vehicleId || appContext.isSidebarOpen !== formId) return

    setLoadingIoT(true)
    const url = apiEndpoints.vehicleregistry.getiotodometerstatus.replace("{id}", String(service?.vehicleId))
    iotStatusApi.get(url)
  }, [service?.vehicleId, appContext.isSidebarOpen, iotStatusApi])

  useEffect(() => {
    if (iotStatusResult.status === 2 && iotStatusResult.data) {
      setIotStatus(iotStatusResult.data)
      setLoadingIoT(false)

      // If IoT device has current odometer, update the form
      if (iotStatusResult.data.hasIoTDevice && iotStatusResult.data.currentOdometer) {
        setInitialFormikValues(prev => ({
          ...prev,
          currentMeterSetting: Math.round(iotStatusResult.data.currentOdometer)
        }))
      }
    } else if (iotStatusResult.status === 3) {
      setLoadingIoT(false)
      setIotStatus({ hasIoTDevice: false, currentOdometer: null, lastUpdated: null, isDataRecent: false })
    }
  }, [iotStatusResult])

  useEffect(() => {
    if (!appContext.currentCompanyId) return
    const url = apiEndpoints.vehicleregistrychecks?.getactivecompanycarworkshops + "?companyId=" + appContext.currentCompanyId
    workshopsApi.get(url)
  }, [workshopsApi, appContext.currentCompanyId])

  useEffect(() => {
    if (workshopsResult.status === 2 && workshopsResult.data) {
      setWorkshops(workshopsResult.data)
    }
  }, [workshopsResult])

  const formData = {
    formId: formId,
    titleNew: t('checks.confirm_service', 'Confirm service'),
    titleEdit: t('checks.confirm_service', 'Confirm service'),
    subTitleNew: t('checks.confirm_service_for', 'Confirm service completion for') + ` ${service?.vehicleRegNumber || t("global.vehicle", "Vehicle")}`,
    subTitleEdit: t('checks.confirm_service_for', 'Confirm service completion for') + ` ${service?.vehicleRegNumber || t("global.vehicle", "Vehicle")}`,
    submitText: t('checks.confirm_service', 'Confirm Service'),
    submitEditText: t('checks.confirm_service', 'Confirm service'),
    icon: faWrench,
  }

  enum serviceIntervalType {
    Distance = "Distance",
    Days = "Days",
    Mixed = "Mixed"
  }
  const hasDistanceInterval =
    service?.intervalType === serviceIntervalType.Distance ||
    service?.intervalType === serviceIntervalType.Mixed
  const shouldValidateDistance = hasDistanceInterval && !iotStatus?.hasIoTDevice

  // Show loading state while fetching data
  if (appContext.isSidebarOpen === formId && serviceDataResult.status === 1) {
    return (
      <Sidebar
        initialFormikValues={initialFormikValues}
        setInitialFormikValues={setInitialFormikValues}
        formResult={intervalApiResult}
        formData={formData}
        handleSubmit={handleSubmit}
        validationSchema={ServiceConfirmationValidationSchema2(t, hasDistanceInterval, service?.currentOdometerReading, !!iotStatus?.hasIoTDevice)}
        formRef={formRef}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </Sidebar>
    )
  }

  // Show error state if data fetch failed
  if (appContext.isSidebarOpen === formId && serviceDataResult.status === 3) {
    return (
      <Sidebar
        initialFormikValues={initialFormikValues}
        setInitialFormikValues={setInitialFormikValues}
        formResult={intervalApiResult}
        formData={formData}
        handleSubmit={handleSubmit}
        validationSchema={ServiceConfirmationValidationSchema2(t, hasDistanceInterval, service?.currentOdometerReading, !!iotStatus?.hasIoTDevice)}
        formRef={formRef}
      >
        <Alert variant="outlined" severity="error">
          {t("checks.failed_to_load_service", "Failed to load service data. Please try again.")}
        </Alert>
      </Sidebar>
    )
  }

  return (
    <Sidebar
      initialFormikValues={initialFormikValues}
      setInitialFormikValues={setInitialFormikValues}
      formResult={intervalApiResult}
      formData={formData}
      handleSubmit={handleSubmit}
      validationSchema={ServiceConfirmationValidationSchema2(t, hasDistanceInterval, service?.currentOdometerReading, !!iotStatus?.hasIoTDevice)}
      formRef={formRef}
    >
      <StyledContainer>
        <ServiceInfoBox service={service} />

        <StyledFormSection>
          <ServiceDateTextField />
          {hasDistanceInterval && (
            <ServiceMeterTextField />
          )}

          {service?.intervalType !== serviceIntervalType.Days && (
            loadingIoT  ? (
              <Box display='flex' justifyContent='center' py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <CurrentMeterTextField
                iotStatus={iotStatus}
              />
            )
          )}

          <WorkshopField workshops={workshops} />
          <RemarkTextField />
        </StyledFormSection>
        <StyledAccordion disableGutters square>
          <StyledAccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={{ color: theme.palette.text.primary }} />
            }
          >
            <Typography color="text.primary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                color={(theme.palette as any).primary[700]}
                style={{ paddingTop: theme.spacing(0.5) }}
              />
              {t("global.important_information", "Important Information")}
            </Typography>
          </StyledAccordionSummary>

          <StyledAccordionDetails>
            <Typography
              color="text.primary"
              variant="bodyMedium">
              {t("checks.mark_service_complete", "This action marks the service as complete. The next service deadline is automatically calculated based on the template's settings.")}
            </Typography>
          </StyledAccordionDetails>
        </StyledAccordion>

      </StyledContainer>
    </Sidebar>
  )
}

export default ServiceConfirmationSidebar
